import React from "react";
import Select from "react-select";
import PaginatedSelectMenuList from "components/atoms/PaginatedSelectMenuList";
import useIsSmallDevice from "customsHooks/useSmallDevice";
import { getMenuPortalTarget } from "utils/selectHelpers";

/**
 * react-select with the shared pagination and menu portal behavior.
 * All remaining props are forwarded to react-select unchanged.
 */
const PaginatedSelect = ({
  loadMore,
  loadInitialDebounced,
  onInputChange,
  components,
  styles,
  menuPortalTarget,
  ...props
}) => {
  const { isMinSmallDevice } = useIsSmallDevice();

  let resolvedMenuPortalTarget = menuPortalTarget;
  if (resolvedMenuPortalTarget === undefined) {
    resolvedMenuPortalTarget = getMenuPortalTarget(isMinSmallDevice);
  }

  /**
   * Handles the input change event for the select component.
   * @param {string} inputValue - The current value of the input field.
   * @param {object} actionMeta - Metadata about the input change action.
   * @returns {string} - The processed input value.
   */
  const handleInputChange = (inputValue, actionMeta) => {
    const result = onInputChange?.(inputValue, actionMeta);
    if (!onInputChange && actionMeta?.action === "input-change") {
      loadInitialDebounced?.(inputValue);
    }
    return result ?? inputValue;
  };

  return (
    <Select
      {...props}
      components={{ ...components, MenuList: PaginatedSelectMenuList }}
      onPaginatedSelectLoadMore={loadMore ?? props.onMenuScrollToBottom}
      onMenuScrollToBottom={loadMore ?? props.onMenuScrollToBottom}
      onInputChange={handleInputChange}
      captureMenuScroll={false}
      menuPortalTarget={resolvedMenuPortalTarget}
      styles={{
        ...styles,
        menuPortal: (base, state) => ({
          ...base,
          ...styles?.menuPortal?.(base, state),
          zIndex: 9999,
        }),
      }}
    />
  );
};

export default PaginatedSelect;
